import { useState } from "react";
import { ArrowUpRight, Star } from "lucide-react";
import { projects } from "@/data/projects";

// Take first 5 projects for the case-study list
const cases = projects.slice(0, 5).map((p, i) => ({
    n: String(i + 1).padStart(2, "0"),
    title: p.title,
    tag: p.category,
    subtitle: p.subtitle,
    slug: p.slug,
    image: p.image,
}));

export default function DigtekCaseStudies() {
    const [active, setActive] = useState(0);
    const current = cases[active];

    return (
        <section
            id="work"
            data-testid="digtek-case-studies"
            className="relative py-24 lg:py-32 bg-digtek-header overflow-hidden text-white"
        >
            <div className="digtek-blob purple w-[500px] h-[500px] left-1/2 -translate-x-1/2 top-0 opacity-40" />
            <div
                aria-hidden
                className="absolute top-12 left-6 lg:left-10 digtek-dot-ring opacity-30 w-40 h-40"
            />

            <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 items-end gap-6 mb-14">
                    <div className="lg:col-span-8">
                        <div className="digtek-eyebrow mb-4 !text-digtek-lime before:!bg-digtek-lime">
                            <span>Case studies</span>
                        </div>
                        <h2
                            data-testid="cases-heading"
                            className="font-display font-bold text-white text-[42px] sm:text-[54px] lg:text-[64px] leading-[1.02]"
                        >
                            We're a creative studio
                            <br />
                            wired for{" "}
                            <em className="not-italic text-digtek-lime">
                                growth math
                            </em>
                            .
                        </h2>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end">
                        <a
                            href="/#work"
                            data-testid="cases-cta"
                            className="btn-digtek-lime"
                        >
                            Explore more
                            <ArrowUpRight className="w-4 h-4" />
                        </a>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
                    {/* Left — active image */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <img
                                src={current.image}
                                alt={current.title}
                                className="w-full aspect-[4/5] object-cover rounded-3xl"
                            />
                            <div className="absolute top-5 right-5 flex flex-wrap gap-2">
                                <span className="text-[11px] uppercase tracking-wider font-bold bg-digtek-lime text-digtek-header px-3 py-1.5 rounded-full">
                                    {current.tag}
                                </span>
                            </div>
                            <a
                                href={`/work/${current.slug}`}
                                className="absolute bottom-5 right-5 w-14 h-14 rounded-full bg-digtek-lime text-digtek-header flex items-center justify-center hover:bg-white transition-colors"
                            >
                                <ArrowUpRight className="w-6 h-6" />
                            </a>
                        </div>
                    </div>

                    {/* Right — numbered list */}
                    <div className="lg:col-span-7">
                        <ul>
                            {cases.map((c, i) => (
                                <li
                                    key={c.slug}
                                    data-testid={`case-row-${i}`}
                                    onMouseEnter={() => setActive(i)}
                                    className={`digtek-case-row group ${
                                        i === active
                                            ? "!border-b-white/40"
                                            : ""
                                    }`}
                                >
                                    <div className="flex items-center gap-6 lg:gap-10">
                                        <span
                                            className={`digtek-case-number font-display font-bold text-[38px] lg:text-[48px] leading-none w-16 shrink-0 ${
                                                i === active
                                                    ? "text-digtek-lime"
                                                    : "text-white/40"
                                            } transition-colors`}
                                        >
                                            {c.n}
                                        </span>
                                        <div className="flex-1">
                                            <a
                                                href={`/work/${c.slug}`}
                                                className={`digtek-case-title block font-display font-bold text-2xl lg:text-3xl leading-tight transition-colors ${
                                                    i === active
                                                        ? "text-digtek-lime"
                                                        : "text-white"
                                                } group-hover:text-digtek-lime`}
                                            >
                                                {c.title}
                                            </a>
                                            <p className="mt-1 text-white/60 text-sm max-w-[440px]">
                                                {c.subtitle}
                                            </p>
                                        </div>
                                        <a
                                            href={`/work/${c.slug}`}
                                            className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 transition-all ${
                                                i === active
                                                    ? "bg-digtek-lime text-digtek-header"
                                                    : "bg-white/10 text-white group-hover:bg-digtek-lime group-hover:text-digtek-header"
                                            }`}
                                            aria-label={`Open ${c.title}`}
                                        >
                                            <ArrowUpRight className="w-5 h-5" />
                                        </a>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Stats strip */}
                <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8 border-t border-white/10 pt-12">
                    {[
                        { n: "500+", l: "Total projects" },
                        { n: "300M+", l: "Views generated" },
                        { n: "4.9/5", l: "Client rating" },
                        { n: "12+", l: "Industries served" },
                    ].map((s) => (
                        <div key={s.l}>
                            <p className="font-display font-bold text-4xl lg:text-5xl text-digtek-lime leading-none">
                                {s.n}
                            </p>
                            <p className="mt-2 text-white/70 text-sm">{s.l}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
